/**
 * Primitives géométriques.
 *
 * Portage direct du `companion object` de `GpxTrack.kt` et de
 * `SnapToRoad.computeBearing`. Les constantes sont reprises telles quelles :
 * elles sont verrouillées par les tests de référence de l'application Android
 * et les modifier ferait diverger les distances et dénivelés affichés.
 */

/** Rayon terrestre utilisé par `haversine` (sphère). */
export const EARTH_RADIUS_M = 6_371_000.0

/**
 * Mètres par degré de latitude, utilisé par tous les pré-filtres de boîte
 * englobante et le dead-reckoning.
 *
 * Cette valeur correspond à un rayon d'environ 6 378 137 m, donc à un ellipsoïde
 * différent de celui de `haversine` ci-dessus. L'incohérence est reprise
 * volontairement de l'application Android : les seuils de projection (18 m,
 * 20 m, 25 m) ont été réglés avec ces deux constantes en place, et les
 * harmoniser déplacerait les points d'accrochage.
 */
export const METERS_PER_DEGREE_LAT = 111_320.0

export interface LatLng {
  readonly lat: number
  readonly lon: number
}

const DEG_TO_RAD = Math.PI / 180
const RAD_TO_DEG = 180 / Math.PI

export function toRadians(degrees: number): number {
  return degrees * DEG_TO_RAD
}

export function toDegrees(radians: number): number {
  return radians * RAD_TO_DEG
}

/** Distance orthodromique en mètres. */
export function haversine(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const dLat = toRadians(lat2 - lat1)
  const dLon = toRadians(lon2 - lon1)

  const sinLat = Math.sin(dLat / 2)
  const sinLon = Math.sin(dLon / 2)

  const a =
    sinLat * sinLat +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * sinLon * sinLon

  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export interface SegmentProjection {
  readonly lat: number
  readonly lon: number
  /** Position sur le segment, dans [0, 1]. */
  readonly t: number
}

/**
 * Projette un point sur un segment, en coordonnées planaires (degrés).
 *
 * La distorsion longitude/latitude n'est délibérément pas corrigée : le `t`
 * obtenu sert uniquement à interpoler, et la distance réelle est ensuite
 * mesurée avec `haversine`. « Corriger » la projection décalerait les points
 * d'accrochage par rapport à l'application Android.
 */
export function projectOnSegment(
  lat: number,
  lon: number,
  p1Lat: number,
  p1Lon: number,
  p2Lat: number,
  p2Lon: number,
): SegmentProjection {
  const dx = p2Lon - p1Lon
  const dy = p2Lat - p1Lat
  const segLenSq = dx * dx + dy * dy

  // Segment dégénéré (deux points identiques) : on retombe sur son origine.
  if (segLenSq < 1e-12) {
    return { lat: p1Lat, lon: p1Lon, t: 0 }
  }

  const px = lon - p1Lon
  const py = lat - p1Lat

  let t = (px * dx + py * dy) / segLenSq
  if (t < 0) t = 0
  else if (t > 1) t = 1

  return { lat: p1Lat + t * dy, lon: p1Lon + t * dx, t }
}

/**
 * Cap initial orthodromique de p1 vers p2, en degrés dans [0, 360[.
 */
export function computeBearing(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const dLon = toRadians(lon2 - lon1)
  const rLat1 = toRadians(lat1)
  const rLat2 = toRadians(lat2)

  const x = Math.sin(dLon) * Math.cos(rLat2)
  const y =
    Math.cos(rLat1) * Math.sin(rLat2) -
    Math.sin(rLat1) * Math.cos(rLat2) * Math.cos(dLon)

  return (toDegrees(Math.atan2(x, y)) + 360) % 360
}

/**
 * Ramène un écart d'angle dans ]-180, 180].
 *
 * La borne est fermée à +180 et ouverte à -180 : c'est ce qui fait qu'un écart
 * de très exactement 90° ne déclenche pas de retournement dans `alignBearing`.
 */
export function normalizeBearingDelta(delta: number): number {
  let d = delta
  while (d > 180) d -= 360
  while (d <= -180) d += 360
  return d
}

/**
 * Retourne le cap d'un segment de 180° s'il s'oppose au cap GPS.
 *
 * Un segment de trace n'a pas de sens de parcours : si l'on roule dans le sens
 * inverse de sa numérotation, son cap pointe à l'opposé du déplacement réel.
 *
 * Le seuil est strict : un écart d'exactement 90° ne retourne pas le cap.
 */
export function alignBearing(
  segmentBearing: number,
  gpsBearing: number,
): number {
  const diff = normalizeBearingDelta(segmentBearing - gpsBearing)
  return Math.abs(diff) > 90 ? (segmentBearing + 180) % 360 : segmentBearing
}

/**
 * Index du premier élément supérieur ou égal à `value`, par dichotomie.
 *
 * Renvoie `to` si aucun élément ne convient. `from`/`to` délimitent la plage
 * de recherche, `to` exclu.
 */
export function lowerBound(
  values: ArrayLike<number>,
  value: number,
  from = 0,
  to = values.length,
): number {
  let lo = from
  let hi = to

  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if (values[mid]! < value) {
      lo = mid + 1
    } else {
      hi = mid
    }
  }

  return lo
}

/**
 * Marges de boîte englobante correspondant à un rayon en mètres.
 *
 * Le facteur 1,5 est une marge de sécurité reprise de l'application Android ;
 * la division par le cosinus de la latitude compense le rétrécissement des
 * degrés de longitude vers les pôles, plafonné pour ne pas exploser près
 * des pôles.
 */
export function bboxMargins(
  latitude: number,
  radiusMeters: number,
): { latMargin: number; lonMargin: number } {
  const latMargin = (radiusMeters / METERS_PER_DEGREE_LAT) * 1.5
  const lonMargin = latMargin / Math.max(Math.cos(toRadians(latitude)), 0.01)
  return { latMargin, lonMargin }
}

/**
 * Résolution au sol en mètres par pixel, pour des tuiles de 512 px.
 *
 * 78271.517 = circonférence équatoriale (40 075 016,686 m) / 512.
 */
export function metersPerPixel(
  zoom: number,
  latitude: number,
  density = 1,
): number {
  return (78271.517 * Math.cos(toRadians(latitude))) / (2 ** zoom * density)
}
